import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../context/I18nContext";
import { Megaphone, Sparkles, Users, FileText, ArrowRight, CheckCircle2 } from "lucide-react";

export default function Landing() {
  const { t, lang, setLang } = useI18n();

  return (
    <div className="min-h-screen bg-white text-[#09090B]">
      {/* Top nav */}
      <nav className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 klein-blue flex items-center justify-center font-bold text-white" style={{borderRadius: 2}}>R</div>
            <span className="font-display font-bold text-lg">RecruitFlow <span className="text-[#002FA7]">AI</span></span>
          </div>
          <div className="flex items-center gap-4">
            <select value={lang} onChange={(e) => setLang(e.target.value)} data-testid="landing-lang" className="text-sm border border-zinc-200 px-2 py-1 bg-white uppercase" style={{borderRadius: 2}}>
              <option value="fr">FR</option>
              <option value="en">EN</option>
              <option value="hu">HU</option>
              <option value="it">IT</option>
              <option value="de">DE</option>
            </select>
            <Link to="/login" data-testid="nav-login" className="text-sm font-medium text-zinc-700 hover:text-black">{t("login")}</Link>
            <Link to="/pricing" data-testid="nav-pricing" className="text-sm font-medium text-zinc-700 hover:text-black">{t("pricing")}</Link>
            <Link to="/register" data-testid="nav-register" className="btn-primary text-sm" style={{borderRadius: 2}}>{t("get_started")}</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden grain">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 border border-zinc-200 px-3 py-1 mb-6 text-xs uppercase tracking-[0.2em] text-zinc-600" style={{borderRadius: 2}}>
              <span className="w-1.5 h-1.5 bg-[#10B981] rounded-full inline-block"></span> Europe Recruitment · AI
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[0.95] mb-6">
              {t("hero_headline")}
            </h1>
            <p className="text-lg md:text-xl text-zinc-600 max-w-xl leading-relaxed mb-8">
              {t("hero_sub")}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/register" data-testid="hero-cta" className="btn-primary inline-flex items-center gap-2" style={{borderRadius: 2}}>
                {t("get_started")} <ArrowRight size={16}/>
              </Link>
              <Link to="/login" className="px-6 py-2.5 border border-zinc-300 font-medium hover:bg-zinc-50" style={{borderRadius: 2}}>{t("login")}</Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-zinc-600">
              <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#10B981]"/> Facebook · Instagram · TikTok</div>
              <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#10B981]"/> FR · HU · EN</div>
            </div>
          </div>

          <div className="md:col-span-5 fade-up fade-up-2">
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-20 h-20 klein-blue" style={{borderRadius: 2}}></div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-[#E63946]" style={{borderRadius: 2}}></div>
              <img
                src="https://static.prod-images.emergentagent.com/jobs/8029a35e-9ff8-4b36-8115-a88448b16c39/images/180dcca71c15fbfaaa8f8325bbdcd0c3092bf12e0262eeeb544ecc0d45f63b20.png"
                alt="workspace"
                className="relative w-full h-auto object-cover border border-zinc-200 shadow-xl"
                style={{borderRadius: 2}}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-zinc-200 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
          <div className="mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">{t("features")}</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight max-w-3xl">
              Tout ce qu'il faut pour recruter vite, bien, et sans friction.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-zinc-200 bg-white">
            {[
              { icon: Megaphone, title: t("feature_1_title"), desc: t("feature_1_desc") },
              { icon: Sparkles, title: t("feature_2_title"), desc: t("feature_2_desc") },
              { icon: Users, title: t("feature_3_title"), desc: t("feature_3_desc") },
              { icon: FileText, title: t("feature_4_title"), desc: t("feature_4_desc") },
            ].map((f, i) => (
              <div key={i} className="p-8 border-b md:border-b-0 md:border-r border-zinc-200 last:border-r-0 hover:bg-zinc-50 transition-colors">
                <f.icon size={24} className="text-[#002FA7] mb-5"/>
                <div className="font-display font-bold text-xl mb-2">{f.title}</div>
                <div className="text-sm text-zinc-600 leading-relaxed">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#09090B] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight mb-4">{t("get_started")}.</h2>
            <p className="text-zinc-400 max-w-md">{t("signup_subtitle")}</p>
          </div>
          <div className="flex md:justify-end">
            <Link to="/register" data-testid="cta-register" className="bg-white text-black px-6 py-3 font-medium hover:bg-zinc-200 inline-flex items-center gap-2" style={{borderRadius: 2}}>
              {t("register")} <ArrowRight size={16}/>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-200 py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row justify-between items-center gap-3 text-sm text-zinc-500">
          <div>© 2026 RecruitFlow AI</div>
          <div className="uppercase tracking-[0.2em] text-xs">Made for Europe</div>
        </div>
      </footer>
    </div>
  );
}
